<section class="d-flex flex-column gap-2" data-testid="webhook-events-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.ops.webhooks.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.ops.webhooks.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="webhooks-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="webhooks-scope-note">{{ 'backoffice.ops.webhooks.scope_note' | translate }}</p>

  @if (!facade.canReplay()) {
    <p class="av-meta m-0" data-testid="webhooks-restricted">{{ 'backoffice.ops.webhooks.replay_restricted' | translate }}</p>
  }
</section>

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="webhook-events-body">
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.ops.webhooks.error_title' | translate"
        [message]="'backoffice.ops.webhooks.error_message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      <div class="filter-row d-flex flex-wrap align-items-end gap-2">
        <label class="d-flex flex-column gap-1" for="webhook-type">
          <span class="av-meta">{{ 'backoffice.ops.webhooks.filters.event_type' | translate }}</span>
          <select
            id="webhook-type"
            class="av-field"
            [value]="facade.filters().event_type"
            (change)="changeFilter('event_type', $any($event.target).value)"
            data-testid="webhook-type-filter"
          >
            <option value="">{{ 'backoffice.ops.webhooks.filters.any_type' | translate }}</option>
            @for (type of facade.types(); track type) {
              <option [value]="type">{{ type }}</option>
            }
          </select>
        </label>

        <label class="d-flex flex-column gap-1" for="webhook-status">
          <span class="av-meta">{{ 'backoffice.ops.webhooks.filters.processing_status' | translate }}</span>
          <select
            id="webhook-status"
            class="av-field"
            [value]="facade.filters().processing_status"
            (change)="changeFilter('processing_status', $any($event.target).value)"
            data-testid="webhook-status-filter"
          >
            <option value="">{{ 'backoffice.ops.webhooks.filters.any_status' | translate }}</option>
            @for (status of facade.statuses(); track status) {
              <option [value]="status">{{ status }}</option>
            }
          </select>
        </label>

        <label class="d-flex flex-column gap-1" for="webhook-event-id">
          <span class="av-meta">{{ 'backoffice.ops.webhooks.filters.event_id' | translate }}</span>
          <input
            id="webhook-event-id"
            type="search"
            class="av-field"
            [value]="facade.filters().stripe_event_id"
            (change)="changeFilter('stripe_event_id', $any($event.target).value)"
            data-testid="webhook-event-id-filter"
          />
        </label>

        @if (facade.hasActiveFilters()) {
          <button
            type="button"
            class="av-btn"
            (click)="clearFilters()"
            data-testid="webhook-clear-filters"
          >
            {{ 'backoffice.ops.webhooks.filters.clear' | translate }}
          </button>
        }
      </div>

      @if (isEmpty()) {
        <av-empty-state
          icon="webhook"
          [title]="
            (facade.hasActiveFilters()
              ? 'backoffice.ops.webhooks.filtered_empty_title'
              : 'backoffice.ops.webhooks.empty_title'
            ) | translate
          "
          [message]="
            (facade.hasActiveFilters()
              ? 'backoffice.ops.webhooks.filtered_empty_message'
              : 'backoffice.ops.webhooks.empty_message'
            ) | translate
          "
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="webhook-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.ops.webhooks.table.event' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.webhooks.table.type' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.webhooks.table.status' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.webhooks.table.livemode' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.ops.webhooks.table.attempts' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.webhooks.table.received' | translate }}</th>
                <th scope="col"></th>
              </tr>
            </thead>

            <tbody>
              @for (event of facade.events(); track event.id) {
                <tr>
                  <td>
                    <button
                      type="button"
                      class="link-button av-source"
                      (click)="selectEvent(event.id)"
                      data-testid="webhook-select"
                    >
                      {{ event.stripe_event_id || event.id }}
                    </button>
                  </td>

                  <td class="cell-muted">{{ event.event_type || unresolved }}</td>

                  <td>
                    <av-status-chip
                      [label]="event.processing_status"
                      [tone]="event.statusTone"
                    ></av-status-chip>
                  </td>

                  <td class="cell-muted">{{ (event.livemode ? 'backoffice.ops.webhooks.live' : 'backoffice.ops.webhooks.test') | translate }}</td>

                  <td class="text-end">{{ event.attempts }}</td>

                  <td class="cell-muted">
                    {{ event.received_at ? (event.received_at | date: dateTimeFormat) : unresolved }}
                  </td>

                  <td>
                    <button
                      type="button"
                      class="av-btn"
                      [disabled]="!facade.canReplay() || !event.is_replayable"
                      [matTooltip]="
                        event.is_replayable
                          ? ''
                          : ('backoffice.ops.webhooks.not_replayable' | translate)
                      "
                      (click)="replay(event.id)"
                      data-testid="webhook-replay"
                    >
                      {{ 'backoffice.ops.webhooks.replay' | translate }}
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.page()"
          [pageSize]="facade.pageSize()"
          [total]="facade.total()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.ops.webhooks.loading_title' | translate"
        [message]="'backoffice.ops.webhooks.loading_message' | translate"
      ></av-empty-state>
    }
  }
</section>

@if (facade.selectedEventId()) {
  <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="webhook-detail">
    <h3 class="av-section-title m-0">{{ 'backoffice.ops.webhooks.detail_title' | translate }}</h3>

    @switch (facade.selectedEventState()) {
      @case ('error') {
        <p class="av-meta m-0" data-testid="webhook-detail-error">{{ 'backoffice.ops.webhooks.error_message' | translate }}</p>
      }
      @case ('ready') {
        @if (facade.selectedEvent(); as event) {
          <div class="d-flex flex-wrap gap-4">
            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.ops.webhooks.detail.stripe_event' | translate }}</span>
              <span class="av-source">{{ event.stripe_event_id || unresolved }}</span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.ops.webhooks.detail.type' | translate }}</span>
              <span class="cell-strong">{{ event.event_type || unresolved }}</span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.ops.webhooks.detail.status' | translate }}</span>
              <av-status-chip
                [label]="event.processing_status"
                [tone]="event.statusTone"
              ></av-status-chip>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.ops.webhooks.detail.processed' | translate }}</span>
              <span class="cell-strong">
                {{ event.processed_at ? (event.processed_at | date: dateTimeFormat) : unresolved }}
              </span>
            </div>

            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ 'backoffice.ops.webhooks.detail.error' | translate }}</span>
              <span class="cell-strong">{{ event.error_message || unresolved }}</span>
            </div>
          </div>

          <div class="d-flex flex-column gap-1">
            <h4 class="av-section-title m-0">{{ 'backoffice.ops.webhooks.payload_title' | translate }}</h4>
            <p class="av-meta m-0">{{ 'backoffice.ops.webhooks.payload_note' | translate }}</p>
          </div>

          @if (event.hasPayload) {
            <pre class="av-source payload-json" data-testid="webhook-payload">{{
              event.payloadJson
            }}</pre>
          } @else {
            <p class="av-meta m-0" data-testid="webhook-payload-empty">{{ 'backoffice.ops.webhooks.payload_empty' | translate }}</p>
          }
        }
      }
      @default {
        <p class="av-meta m-0">{{ 'backoffice.ops.webhooks.detail_loading' | translate }}</p>
      }
    }
  </section>
}
